import { FC, ReactNode, useState } from 'react';
import { Descriptions, Flex, Tabs } from 'antd';
import { TbActivity, TbFileInfo, TbMessages, TbTextRecognition } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { AttachmentDestination, CloudAttachment } from '@/services';
import ResourceActivityFeed from '@/shared/components/activity/ResourceActivityFeed';
import ResourceCommentsPanel from '@/shared/components/activity/ResourceCommentsPanel';

import { resourceTypeForAttachmentDestination } from '../attachmentResource';

import './index.css';

interface AttachmentDocumentSiderProps {
    activeConceptReferenceId?: string;
    attachment: CloudAttachment;
    canComment?: boolean;
    collapsed?: boolean;
    concepts?: ReactNode;
    destination?: AttachmentDestination;
}

const AttachmentDocumentSider: FC<AttachmentDocumentSiderProps> = ({
    activeConceptReferenceId,
    attachment,
    canComment,
    collapsed = false,
    concepts,
    destination,
}) => {
    const { token } = useAntDesignTheme();
    const resourceType = destination ? resourceTypeForAttachmentDestination(destination) : undefined;
    const hasConcepts = Boolean(concepts);
    const [tabSelection, setTabSelection] = useState({
        key: hasConcepts ? 'concepts' : destination ? 'comments' : 'details',
        referenceId: activeConceptReferenceId,
    });
    const focusNewConceptReference = Boolean(
        hasConcepts && activeConceptReferenceId && activeConceptReferenceId !== tabSelection.referenceId,
    );
    const activeTab = focusNewConceptReference ? 'concepts' : tabSelection.key;

    if (collapsed) {
        return null;
    }

    return (
        <aside
            aria-label='Attachment review'
            style={{
                borderLeft: `1px solid ${token.colorBorder}`,
                display: 'flex',
                flex: '0 0 min(20rem, 38%)',
                flexDirection: 'column',
                minHeight: 0,
                minWidth: '16rem',
                overflow: 'hidden',
            }}
        >
            <Tabs
                activeKey={activeTab}
                className='attachment-document-sider-tabs'
                size='small'
                style={{ flex: '1 1 0%', minHeight: 0 }}
                tabBarStyle={{ marginBottom: 0, padding: '0 0.5rem' }}
                onChange={(key) => setTabSelection({ key, referenceId: activeConceptReferenceId })}
                items={[
                    ...(concepts
                        ? [
                              {
                                  children: concepts,
                                  key: 'concepts',
                                  label: (
                                      <Flex align='center' gap='0.375rem'>
                                          <TbTextRecognition />
                                          Concepts
                                      </Flex>
                                  ),
                              },
                          ]
                        : []),
                    {
                        key: 'details',
                        label: (
                            <Flex align='center' gap='0.375rem'>
                                <TbFileInfo />
                                Details
                            </Flex>
                        ),
                        children: (
                            <Descriptions
                                column={1}
                                size='small'
                                layout='vertical'
                                style={{ padding: '1rem' }}
                                items={[
                                    { key: 'name', label: 'Name', children: attachment.name },
                                    { key: 'type', label: 'File Type', children: attachment.mimeType },
                                    ...(attachment.createdTime
                                        ? [
                                              {
                                                  key: 'created',
                                                  label: 'Created',
                                                  children: new Date(attachment.createdTime).toLocaleDateString(),
                                              },
                                          ]
                                        : []),
                                ]}
                            />
                        ),
                    },
                    ...(destination && resourceType
                        ? [
                              {
                                  children: (
                                      <div className='attachment-document-sider-panel'>
                                          <ResourceCommentsPanel
                                              canComment={canComment}
                                              component={`attachment:${attachment.id}`}
                                              height='100%'
                                              resourceId={destination.resourceId}
                                              resourceTitle={attachment.name}
                                              resourceType={resourceType}
                                          />
                                      </div>
                                  ),
                                  key: 'comments',
                                  label: (
                                      <Flex align='center' gap='0.375rem'>
                                          <TbMessages />
                                          Comments
                                      </Flex>
                                  ),
                              },
                              {
                                  children: (
                                      <div className='attachment-document-sider-panel'>
                                          <ResourceActivityFeed
                                              height='100%'
                                              resourceId={destination.resourceId}
                                              resourceTitle={destination.resourceName}
                                              resourceType={resourceType}
                                          />
                                      </div>
                                  ),
                                  key: 'activity',
                                  label: (
                                      <Flex align='center' gap='0.375rem'>
                                          <TbActivity />
                                          Activity
                                      </Flex>
                                  ),
                              },
                          ]
                        : []),
                ]}
            />
        </aside>
    );
};

export default AttachmentDocumentSider;
